import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { meQueryKey, useMe } from "@/api/households";
import { useAuth } from "@/auth/AuthProvider";
import { browserLocale, resolveLocale, type Locale } from "./locales";
import { translatorFor, type Translate } from "./translate";

interface LocaleValue {
  locale: Locale;
  t: Translate;
  setLocale: (locale: Locale) => void;
  changing: boolean;
}

const LocaleContext = createContext<LocaleValue | undefined>(undefined);

/**
 * Renders its children in one named language.
 *
 * This is the half of the provider that has nothing to do with *where* the
 * language came from, which is why it is separate: a test — or a screenshot, or
 * a future preview — can say "render this in English" without an identity
 * provider, a query client and a signed-in user standing behind it.
 */
export function FixedLocale({
  locale,
  setLocale = () => {},
  changing = false,
  children,
}: {
  locale: Locale;
  setLocale?: (locale: Locale) => void;
  changing?: boolean;
  children: ReactNode;
}) {
  // Screen readers and browser translation both read this attribute, and a page
  // of German text announced as English is worse than no attribute at all.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleValue>(
    () => ({ locale, t: translatorFor(locale), setLocale, changing }),
    [locale, setLocale, changing],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Decides which language everything below it is written in (K-36).
 *
 * Two sources, in this order:
 *
 *  1. **The stored choice.** `user.locale` is what the person picked, and it is
 *     per person rather than per household — one member of a German household
 *     reading English does not move anybody else's screen.
 *  2. **The browser**, until `/me` has answered. The sign-in screen is rendered
 *     before Kontora knows who is reading it, and greeting somebody in the wrong
 *     language is a bad first sentence.
 *
 * The stored choice wins the moment it arrives, including when it disagrees with
 * the browser: somebody who chose English on a German laptop meant it.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { data: me } = useMe(isAuthenticated);
  const client = useQueryClient();

  const locale: Locale = me?.locale ? resolveLocale(me.locale) : browserLocale();

  const change = useMutation({
    mutationFn: (next: Locale) =>
      apiFetch<{ locale: string }>("/api/v1/me/locale", {
        method: "PUT",
        body: { locale: next },
      }),
    // The server owns the answer: it is the thing that refused an unsupported
    // language, so re-reading is how the screen learns whether the change took.
    onSuccess: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });

  const setLocale = useCallback((next: Locale) => change.mutate(next), [change]);

  return (
    <FixedLocale locale={locale} setLocale={setLocale} changing={change.isPending}>
      {children}
    </FixedLocale>
  );
}

/** The language this part of the interface is written in, and how to say things. */
export function useI18n(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) {
    throw new Error("useI18n was called outside LocaleProvider");
  }
  return value;
}

/** Shorthand for the common case: a component that only needs to say things. */
export function useT(): Translate {
  return useI18n().t;
}
